import React, { useState, useEffect } from 'react';
import axios from 'axios';
import DashboardLayout from '../components/layout/DashboardLayout';
import { EmptyState } from '@/components/shared/EmptyState';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { formatCurrency, cn } from '@/lib/utils';
import {
  AlertTriangle,
  CheckCircle,
  Info,
  Bell,
  Filter,
  Search,
  ShieldCheck,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const severityConfig = {
  critical: {
    icon: AlertTriangle,
    bg: 'bg-red-50',
    border: 'border-red-200',
    iconColor: 'text-red-600',
    badge: 'bg-red-100 text-red-700 border-red-300',
  },
  warning: {
    icon: AlertTriangle,
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    iconColor: 'text-amber-600',
    badge: 'bg-amber-100 text-amber-700 border-amber-300',
  },
  info: {
    icon: Info,
    bg: 'bg-sky-50',
    border: 'border-sky-200',
    iconColor: 'text-sky-600',
    badge: 'bg-sky-100 text-sky-700 border-sky-300',
  },
};

export default function AlertsPage() {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [severityFilter, setSeverityFilter] = useState('all');

  useEffect(() => {
    fetchAlerts();
  }, []);

  const fetchAlerts = async () => {
    try {
      const response = await axios.get(`${API}/alerts`);
      setAlerts(response.data);
    } catch (error) {
      console.error('Failed to fetch alerts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAcknowledge = async (alertId) => {
    try {
      await axios.patch(`${API}/alerts/${alertId}/acknowledge`);
      setAlerts((prev) =>
        prev.map((a) => (a.id === alertId ? { ...a, acknowledged: true } : a))
      );
    } catch (error) {
      console.error('Failed to acknowledge alert:', error);
    }
  };

  const filteredAlerts = alerts.filter((alert) => {
    const matchesSearch =
      searchQuery === '' ||
      alert.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      alert.asset_name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesSeverity = severityFilter === 'all' || alert.severity === severityFilter;
    return matchesSearch && matchesSeverity;
  });

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin w-8 h-8 border-4 border-sky-500 border-t-transparent rounded-full" />
        </div>
      </DashboardLayout>
    );
  }

  const criticalCount = alerts.filter((a) => a.severity === 'critical' && !a.acknowledged).length;
  const warningCount = alerts.filter((a) => a.severity === 'warning' && !a.acknowledged).length;

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl font-bold text-slate-900">Alerts</h1>
            <p className="text-slate-500">{filteredAlerts.length} alerts</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 px-3 py-1.5 bg-red-50 rounded-lg">
              <AlertTriangle className="w-4 h-4 text-red-600" />
              <span className="text-sm font-medium text-red-700">{criticalCount} Critical</span>
            </div>
            <div className="flex items-center gap-2 px-3 py-1.5 bg-amber-50 rounded-lg">
              <AlertTriangle className="w-4 h-4 text-amber-600" />
              <span className="text-sm font-medium text-amber-700">{warningCount} Warning</span>
            </div>
          </div>
        </div>

        {/* Filters */}
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search alerts..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="alert-search"
                />
              </div>
              <Select value={severityFilter} onValueChange={setSeverityFilter}>
                <SelectTrigger className="w-[150px]" data-testid="severity-filter">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Severity" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Severity</SelectItem>
                  <SelectItem value="critical">Critical</SelectItem>
                  <SelectItem value="warning">Warning</SelectItem>
                  <SelectItem value="info">Info</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Alerts List */}
        <div className="space-y-4" data-testid="alerts-list">
          {filteredAlerts.length === 0 ? (
            <Card className="border-slate-200">
              <CardContent className="p-0">
                <EmptyState
                  icon={ShieldCheck}
                  title={searchQuery ? "No alerts match your filter" : "All clear"}
                  description={searchQuery ? "Try adjusting your search or filters." : "No active alerts — your fleet is running smoothly."}
                />
              </CardContent>
            </Card>
          ) : (
            filteredAlerts.map((alert) => {
              const config = severityConfig[alert.severity] || severityConfig.info;
              const Icon = config.icon;

              return (
                <Card
                  key={alert.id}
                  className={cn('border', config.border, alert.acknowledged && 'opacity-60')}
                  data-testid={`alert-${alert.id}`}
                >
                  <CardContent className={cn('p-5', config.bg)}>
                    <div className="flex items-start gap-4">
                      <div className={cn('p-2 rounded-full', config.bg)}>
                        <Icon className={cn('w-5 h-5', config.iconColor)} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-mono text-sm font-semibold text-slate-900">
                            {alert.asset_name}
                          </span>
                          <Badge variant="outline" className={cn('text-xs', config.badge)}>
                            {alert.severity.toUpperCase()}
                          </Badge>
                          {alert.acknowledged && (
                            <Badge variant="outline" className="text-xs bg-emerald-50 text-emerald-700 border-emerald-300">
                              <CheckCircle className="w-3 h-3 mr-1" />
                              Acknowledged
                            </Badge>
                          )}
                        </div>
                        <h3 className="font-medium text-slate-900 mt-1">{alert.title}</h3>
                        <p className="text-sm text-slate-600 mt-1">{alert.description}</p>
                        
                        {alert.ai_recommendation && (
                          <div className="mt-3 p-3 bg-sky-50 border border-sky-200 rounded-lg">
                            <p className="text-xs font-medium text-sky-800">AI Recommendation:</p>
                            <p className="text-sm text-sky-700 mt-1">{alert.ai_recommendation}</p>
                          </div>
                        )}
                        
                        <div className="mt-4 flex items-center gap-3">
                          {!alert.acknowledged && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleAcknowledge(alert.id)}
                              data-testid={`acknowledge-${alert.id}`}
                            >
                              <CheckCircle className="w-4 h-4 mr-2" />
                              Acknowledge
                            </Button>
                          )}
                          <Button size="sm" data-testid={`create-wo-${alert.id}`}>
                            Create Work Order
                          </Button>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className={cn('text-sm font-semibold', alert.revenue_impact > 0 ? 'text-red-600' : 'text-emerald-600')}>
                          {alert.revenue_impact > 0 ? '-' : '+'}
                          {formatCurrency(Math.abs(alert.revenue_impact))}
                        </p>
                        <p className="text-xs text-slate-500">per day</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
